import { z } from "zod";

// =============================================================================
// RBAC – Rollen und Rechte innerhalb eines Mandanten (Entscheidung 23, Frage 19)
// =============================================================================
// RLS trennt Mandanten (Entscheidung 2). WAS ein Benutzer innerhalb seines
// Mandanten darf, ist Anwendungslogik und wird hier abgebildet.
//
// v1-Modell (D1=A): drei SYSTEMROLLEN, deren Rechte im CODE abgeleitet werden –
// keine DB-Tabellen fuer Rollen/Rechte, keine Cross-Schema-Kopplung. Mandanten-
// eigene Rollen (dann `app.roles`/`app.role_permissions` mit RLS) kommen additiv,
// sobald ein Mandant sie real braucht.
//
// Dieser Katalog ist die SINGLE SOURCE OF TRUTH fuer Rechte: API-Endpunkte und
// (spaeter) KI-Werkzeuge deklarieren ihr benoetigtes Recht typsicher gegen diese
// Liste. Die Pruefung passiert zentral in der Service-Schicht (`authorize()`,
// Phase 5), nie im Frontend und nie im Prompt.

// -----------------------------------------------------------------------------
// Rechte-Katalog: Muster `ressource:aktion`
// -----------------------------------------------------------------------------
// Waechst mit dem Datenmodell (z. B. `devices:*`, `workflows:*` in spaeteren
// Phasen). Neue Rechte hier ergaenzen – dann sind sie ueberall typsicher nutzbar.
export const PERMISSIONS = [
  "orders:read",
  "orders:write",
  "members:read",
  "members:invite",
  "members:manage",
  "config:admin",
  "ai:use", // eingebetteter Chat (F2)
  "ai:external", // externer MCP-Zugang (F1)
] as const;

// Union-Typ aller gueltigen Rechte-Strings. Ein Tippfehler ist ein Compile-Fehler.
export type Permission = (typeof PERMISSIONS)[number];

// Zod-Schema fuer die Validierung an Grenzen (z. B. Werkzeug-Deklaration).
export const permissionSchema = z.enum(PERMISSIONS);

// -----------------------------------------------------------------------------
// Systemrollen (v1). Werden beim Mandanten-Onboarding zugewiesen.
// -----------------------------------------------------------------------------
export const SYSTEM_ROLES = ["tenant_admin", "operator", "viewer"] as const;
export type SystemRole = (typeof SYSTEM_ROLES)[number];

// Zod-Schema: validiert den `role`-Claim aus dem Token und `memberships.role`.
export const systemRoleSchema = z.enum(SYSTEM_ROLES);

// -----------------------------------------------------------------------------
// Rolle -> Rechte. Im Code (Entscheidung 23). Das Access Token traegt nur den
// Rollennamen (Variante 2); die Rechte werden hieraus pro Request abgeleitet –
// kleines Token, Rollendefinition sofort aenderbar ohne Re-Login, kein DB-Lookup.
// -----------------------------------------------------------------------------
export const ROLE_PERMISSIONS: Record<SystemRole, readonly Permission[]> = {
  // Mandanten-Admin: alles (inkl. Mitgliederverwaltung, Konfiguration, externer
  // KI-Zugang). Entscheidet je Rolle, wer KI nutzen/extern anbinden darf.
  tenant_admin: [...PERMISSIONS],
  // Operator: operatives Lesen/Schreiben der Fachdaten + eingebetteter Chat.
  operator: ["orders:read", "orders:write", "members:read", "ai:use"],
  // Viewer: nur Lesen.
  viewer: ["orders:read", "members:read"],
};

// Rechte einer Rolle (fuer das Pre-Token-Lambda bzw. die UI-Rechteanzeige).
export function permissionsForRole(role: SystemRole): readonly Permission[] {
  return ROLE_PERMISSIONS[role];
}

// Kernpruefung: hat die Rolle genau dieses Recht? Basis fuer `authorize()`.
export function roleHasPermission(role: SystemRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

// Type-Guard: ist der (z. B. aus einem Token/DB gelesene) String eine Systemrolle?
export function isSystemRole(value: string): value is SystemRole {
  return (SYSTEM_ROLES as readonly string[]).includes(value);
}
